import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, CheckCircle, Clock, XCircle } from 'lucide-react';
import { gameData, getGameProgress, mockApiCalls } from '../data/mock';
import mapBackgroundImage from "../assets/map/new_map_compressed.jpg";
import { RetryRequestModal } from './ui/RetryRequestModal';

const WorldMap = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState({ capturedDinosaurs: [], failedLocations: [] });
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [catalystUserId, setCatalystUserId] = useState(null);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Retry request modal state
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryStatus, setRetryStatus] = useState('NONE');
  const [retryCount, setRetryCount] = useState(0);
  const [existingFeedback, setExistingFeedback] = useState(null);

  // Format time helper
  const formatTime = (ms) => {
    if (ms == null || ms <= 0) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    let isMounted = true;

    const loadProgress = async () => {
      setIsLoading(true);


      try {
        // Load game progress (always fetch fresh data to reflect any admin resets)
        const savedProgress = await getGameProgress(true);
        if (!isMounted) return;

        setProgress(savedProgress);

        // Check if all dinosaurs are captured
        if (savedProgress.capturedDinosaurs.length === gameData.totalDinosaurs) {
          navigate('/victory');
          return;
        }

        // Fetch time remaining and catalyst user ID
        try {
          const timeResult = await mockApiCalls.getTrackingTime();
          if (isMounted && timeResult.success && timeResult.data) {
            const timeValue = timeResult.data.timeRemaining;
            if (timeValue != null) {
              if (timeValue <= 0) {
                setIsTimeUp(true);
                setTimeRemaining(0);
              } else {
                setTimeRemaining(timeValue);
              }
            }
            // Store catalyst user ID for editor configuration
            if (timeResult.data.catalystUserId) {
              setCatalystUserId(timeResult.data.catalystUserId);
            }
          }
        } catch (timeError) {
          console.error('Error fetching time:', timeError);
        }
      } catch (error) {
        console.error('Error loading game data:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProgress();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // Timer countdown effect
  useEffect(() => {
    if (timeRemaining == null || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1000) {
          setIsTimeUp(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeRemaining]);

  // Show retry modal when time expires and fetch retry status
  useEffect(() => {
    if (isTimeUp && !showRetryModal) {
      // Fetch retry status when time expires
      const fetchRetryStatus = async () => {
        try {
          const result = await mockApiCalls.getRetryStatus();
          if (result.success) {
            setRetryStatus(result.status || 'NONE');
            setRetryCount(result.requestCount || 0);
            // Set existing feedback if submitted
            if (result.feedback && result.feedback.submitted) {
              setExistingFeedback(result.feedback);
            }
          }
        } catch (error) {
          console.error('Error fetching retry status:', error);
        }
      };
      fetchRetryStatus();
      setShowRetryModal(true);
    }
  }, [isTimeUp, showRetryModal]);

  // Handle retry request submission
  const handleRequestRetry = async () => {
    const result = await mockApiCalls.requestRetryChance();
    if (result.success) {
      setRetryStatus('PENDING');
      setRetryCount(result.requestNumber || retryCount + 1);
    }
    return result;
  };

  const handleLocationClick = async (location) => {
    // Block access if time is up
    if (isTimeUp) {
      return;
    }
    navigate(`/location/${location.id}`, {
      state: { timeRemaining, catalystUserId }
    });
  };

  const isCaptured = (locationId) => {
    return progress.capturedDinosaurs.includes(locationId);
  };

  const isFailed = (locationId) => {
    return (progress.failedLocations || []).includes(locationId);
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 relative overflow-hidden">
        {/* Background world map */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${mapBackgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>

        {/* Loading overlay */}
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="text-center">
            <div className="relative mb-6">
              {/* Outer spinning ring */}
              <div className="w-24 h-24 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mx-auto"></div>
              {/* Inner pulsing circle */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-full animate-pulse"></div>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400 mb-2">
              Loading...
            </h2>
            <p className="text-blue-200 text-sm">Preparing your mission...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 relative overflow-hidden">
      {/* Background world map */}
      <div
        className="absolute inset-0 opacity-90"
        style={{
          backgroundImage: `url(${mapBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      ></div>

      {/* Header */}
      <div className="relative z-10 pt-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400 mb-4">
              DRE World
            </h1>
            <p className="text-xl text-blue-200">Each red dot is a challenger. Click a location to view it and unlock the weapon needed to capture it.</p>
          </div>

          {/* Progress tracker and Timer */}
          <div className="bg-black/60 backdrop-blur-md rounded-xl p-4 max-w-lg mx-auto border border-blue-500/30">
            <div className="flex items-center justify-between">
              <span className="text-blue-200 font-semibold">Dinosaurs Captured:</span>
              <span className="text-2xl font-bold text-green-400">
                {progress.capturedDinosaurs.length} / {gameData.totalDinosaurs}
              </span>
            </div>
            <div className="mt-3 bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-green-500 to-emerald-600 h-full transition-all duration-500 rounded-full"
                style={{ width: `${(progress.capturedDinosaurs.length / gameData.totalDinosaurs) * 100}%` }}
              ></div>
            </div>

            {/* Legend */}
            <div className="mt-3 flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-1.5">
                <Target className="w-4 h-4 text-red-500" />
                <span className="text-gray-300">Active</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Target className="w-4 h-4 text-green-500" />
                <span className="text-gray-300">Captured</span>
              </div>
              <div className="flex items-center gap-1.5">
                <XCircle className="w-4 h-4 text-red-600" fill="currentColor" />
                <span className="text-gray-300">Failed</span>
              </div>
            </div>

            {/* Timer display - only show if time tracking has started */}
            {timeRemaining != null && (
              <div className={`mt-4 flex items-center justify-center gap-2 p-2 rounded-lg ${
                isTimeUp ? 'bg-red-900/50 border border-red-500' :
                timeRemaining < 300000 ? 'bg-orange-900/50 border border-orange-500' :
                'bg-blue-900/50 border border-blue-500/50'
              }`}>
                <Clock className={`w-5 h-5 ${isTimeUp ? 'text-red-400' : timeRemaining < 300000 ? 'text-orange-400' : 'text-cyan-400'}`} />
                <span className={`text-xl font-mono font-bold ${
                  isTimeUp ? 'text-red-400' : timeRemaining < 300000 ? 'text-orange-400' : 'text-cyan-300'
                }`}>
                  {formatTime(timeRemaining)}
                </span>
                {isTimeUp && <span className="text-red-400 font-semibold ml-2">TIME'S UP!</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Interactive Map */}
      <div className="relative z-10 flex items-center justify-center px-6 py-12">
        <div className="max-w-6xl w-full aspect-video relative">
          {/* Location markers */}
          {gameData.locations.map((location) => {
            const captured = isCaptured(location.id);
            const failed = isFailed(location.id);
            const isLocked = (isTimeUp && !captured) || failed;
            return (
              <button
                key={location.id}
                onClick={() => handleLocationClick(location)}
                onMouseEnter={() => setHoveredLocation(location.id)}
                onMouseLeave={() => setHoveredLocation(null)}
                disabled={isLocked}
                className={`absolute transform -translate-x-1/2 -translate-y-1/2 group ${isLocked ? 'cursor-not-allowed opacity-50' : ''}`}
                style={{
                  top: location.position.top,
                  left: location.position.left
                }}
              >
                {/* Marker icon */}
                <div className={`relative ${
                  captured
                    ? 'text-green-500'
                    : failed
                    ? 'text-red-600'
                    : isLocked
                    ? 'text-gray-500'
                    : 'text-red-500 animate-pulse'
                } transform ${!isLocked ? 'group-hover:scale-125' : ''} transition-all duration-300`}>
                  {captured ? (
                    <Target className="w-12 h-12" />
                  ) : failed ? (
                    <XCircle className="w-12 h-12" fill="currentColor" />
                  ) : (
                    <Target className="w-12 h-12" />
                  )}

                  {/* Ping effect for uncaptured (only if not locked and not failed) */}
                  {!captured && !isLocked && !failed && (
                    <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-75"></span>
                  )}
                </div>

                {/* Location info card */}
                {hoveredLocation === location.id && (
                  <div
                    className={`absolute w-64 bg-black/90 backdrop-blur-md rounded-xl p-4 border-2 border-cyan-500/50 shadow-2xl animate-fade-in pointer-events-none ${parseFloat(location.position.left) > 70 ? 'right-full mr-4' : parseFloat(location.position.left) < 30 ? 'left-full ml-4' : 'left-1/2 -translate-x-1/2'} ${parseFloat(location.position.top) > 60 ? 'bottom-full mb-4' : 'top-full mt-4'}`}
                  >
                    <h3 className="text-lg font-bold text-cyan-300 mb-2">{location.name}</h3>
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-300">
                        <span className="text-red-400 font-semibold">Target:</span> {location.dinosaur}
                      </p>
                      <p className="text-gray-300">
                        <span className="text-green-400 font-semibold">Weapon:</span> {location.weapon}
                      </p>
                      {captured && (
                        <p className="text-green-400 font-bold mt-2">✓ Captured</p>
                      )}
                      {failed && (
                        <p className="text-red-400 font-bold mt-2">✗ Failed - No attempts left</p>
                      )}
                      {isLocked && !failed && (
                        <p className="text-red-400 font-bold mt-2">🔒 Time's Up - Locked</p>
                      )}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Retry Request Modal - shown when time expires */}
      <RetryRequestModal
        open={showRetryModal}
        onClose={() => setShowRetryModal(false)}
        capturedCount={progress.capturedDinosaurs.length}
        totalCount={gameData.totalDinosaurs}
        capturedList={progress.capturedDinosaurs.map(locId => {
          const location = gameData.locations.find(loc => loc.id === locId);
          return location ? location.dinosaur : `Location ${locId}`;
        })}
        retryStatus={retryStatus}
        retryCount={retryCount}
        existingFeedback={existingFeedback}
        onRequestRetry={handleRequestRetry}
        onFeedbackUpdate={(feedback) => setExistingFeedback(feedback)}
      />
    </div>
  );
};

export default WorldMap;
